import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, X } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

const EMPTY_FORM = { title: "", body: "" };

export function ComposeCustomerCard() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 350);
  const [suggestions, setSuggestions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!debouncedQuery || customer) { setSuggestions([]); return; }
    apiClient.get("/customers", { params: { q: debouncedQuery, limit: 6 } })
      .then(({ data }) => setSuggestions(data.items || []))
      .catch(() => setSuggestions([]));
  }, [debouncedQuery, customer]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const selectCustomer = (c) => {
    setCustomer(c);
    setQuery(`${c.firstname || ""} ${c.lastname || ""}`.trim());
    setSuggestions([]);
  };

  const clearCustomer = () => {
    setCustomer(null);
    setQuery("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer) { toast.error("Pick a customer first."); return; }
    setSending(true);
    try {
      const { data } = await apiClient.post(`/notifications/customer/${customer.id}`, { title: form.title, body: form.body });
      if (data.dispatch.sent) toast.success(`Sent to ${customer.firstname}.`);
      else toast.warning(`Saved, but not delivered (${data.dispatch.skipped_reason || data.dispatch.mode}).`);
      setForm(EMPTY_FORM);
      clearCustomer();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not send this notification."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="compose-customer-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Send size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Notify a Single Customer</p>
            <p className="mt-0.5 text-xs text-text-muted">A push notification sent directly to one customer's device.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="customer-notify-search">Customer</Label>
            {customer ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2" data-testid="customer-notify-selected">
                <span className="text-sm text-text-main">{customer.firstname} {customer.lastname} — {customer.phone}</span>
                <button type="button" onClick={clearCustomer} data-testid="customer-notify-clear-button">
                  <X size={14} strokeWidth={1.5} className="text-text-muted" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="customer-notify-search" placeholder="Search by name or phone..."
                  value={query} onChange={(e) => setQuery(e.target.value)} data-testid="customer-notify-search-input"
                />
                {suggestions.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-surface shadow-lg" data-testid="customer-notify-suggestions">
                    {suggestions.map((c) => (
                      <button
                        key={c.id} type="button" onClick={() => selectCustomer(c)}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-background"
                        data-testid={`customer-notify-suggestion-${c.id}`}
                      >
                        {c.firstname} {c.lastname} · {c.phone}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <TextField label="Title" required value={form.title} onChange={setField("title")} testId="customer-notify-title" />
          <div className="space-y-1.5">
            <Label htmlFor="customer-notify-body">Message</Label>
            <Textarea id="customer-notify-body" data-testid="customer-notify-body" required rows={3} value={form.body} onChange={setField("body")} placeholder="e.g. Your tiffin is out for delivery!" />
          </div>

          <Button type="submit" variant="accent" className="w-full" disabled={sending || !customer} data-testid="customer-notify-submit-button">
            {sending ? "Sending..." : "Send Notification"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
